import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { SearchInput } from "@/components/SearchInput";

export interface TicketItem {
    id?: string | number;
    key: string;
    title?: string;
    url?: string;
    platform?: string;
    status?: string;
    depends_on?: string | string[];
    subtasks?: TicketItem[];
}

export interface TicketsSectionProps {
    jiraKey?: string | null;
    tickets?: TicketItem[];
    metadataTickets?: TicketItem[];
    onAdd?: (ticket: {
        key: string;
        title?: string;
        parentKey?: string;
    }) => Promise<void> | void;
    onDelete?: (ticketKey: string) => Promise<void> | void;
    canAdd?: boolean;
    canDelete?: boolean;
}

export const detectPlatform = (
    key: string,
    explicitPlatform?: string
): { name: string; badgeClass: string } => {
    if (explicitPlatform) {
        const p = explicitPlatform.toLowerCase();
        if (p.includes("github")) {
            return {
                name: explicitPlatform,
                badgeClass:
                    "bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200",
            };
        }
        if (p.includes("gitlab")) {
            return {
                name: explicitPlatform,
                badgeClass:
                    "bg-orange-100 dark:bg-orange-950/60 text-orange-800 dark:text-orange-300",
            };
        }
        return {
            name: explicitPlatform,
            badgeClass:
                "bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300",
        };
    }

    if (key.includes("github.com") || /^GH-\d+/i.test(key)) {
        return {
            name: "GitHub",
            badgeClass:
                "bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200",
        };
    }

    if (key.includes("gitlab.com") || /^GL-\d+/i.test(key)) {
        return {
            name: "GitLab",
            badgeClass:
                "bg-orange-100 dark:bg-orange-950/60 text-orange-800 dark:text-orange-300",
        };
    }

    if (key.includes("trello.com")) {
        return {
            name: "Trello",
            badgeClass:
                "bg-sky-100 dark:bg-sky-950/60 text-sky-800 dark:text-sky-300",
        };
    }

    return {
        name: "Jira",
        badgeClass:
            "bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300",
    };
};

export const getTicketUrl = (ticket: TicketItem): string => {
    if (ticket.url) {
        return ticket.url;
    }

    const key = ticket.key.trim();
    if (key.startsWith("http://") || key.startsWith("https://")) {
        return key;
    }

    const jiraBase = import.meta.env.VITE_JIRA_URL.replace(/\/$/, "");
    return `${jiraBase}/browse/${encodeURIComponent(key)}`;
};

const normalizeHierarchy = (items: TicketItem[]): TicketItem[] => {
    const map = new Map<string, TicketItem>();
    const roots: TicketItem[] = [];

    items.forEach((item) => {
        map.set(item.key, {
            ...item,
            subtasks: item.subtasks ? [...item.subtasks] : [],
        });
    });

    items.forEach((item) => {
        const current = map.get(item.key);
        if (!current) {
            return;
        }

        const parentKey =
            typeof item.depends_on === "string" ? item.depends_on : null;
        if (parentKey && map.has(parentKey)) {
            const parent = map.get(parentKey);
            if (parent) {
                parent.subtasks = parent.subtasks || [];
                parent.subtasks.push(current);
            }
        } else {
            roots.push(current);
        }
    });

    return roots.length > 0 ? roots : items;
};

const filterTicketsBySearch = (
    items: TicketItem[],
    needle: string
): TicketItem[] => {
    const normalized = needle.trim().toLocaleLowerCase("es");
    if (!normalized) {
        return items;
    }

    return items.reduce<TicketItem[]>((acc, ticket) => {
        const filteredSubtasks = ticket.subtasks
            ? filterTicketsBySearch(ticket.subtasks, normalized)
            : [];
        const matchesSelf = `${ticket.key} ${ticket.title ?? ""}`
            .toLocaleLowerCase("es")
            .includes(normalized);

        if (matchesSelf || filteredSubtasks.length > 0) {
            acc.push({
                ...ticket,
                subtasks:
                    filteredSubtasks.length > 0
                        ? filteredSubtasks
                        : ticket.subtasks,
            });
        }

        return acc;
    }, []);
};

export const parseTicketsList = (
    rawJiraKey?: string | null,
    providedTickets?: TicketItem[],
    metadataTickets?: TicketItem[]
): TicketItem[] => {
    if (providedTickets && providedTickets.length > 0) {
        return normalizeHierarchy(providedTickets);
    }

    if (metadataTickets && metadataTickets.length > 0) {
        return normalizeHierarchy(metadataTickets);
    }

    if (!rawJiraKey || !rawJiraKey.trim()) {
        return [];
    }

    const raw = rawJiraKey.trim();

    if (raw.startsWith("[") || raw.startsWith("{")) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return normalizeHierarchy(parsed);
            }
            if (typeof parsed === "object" && parsed !== null) {
                return normalizeHierarchy([parsed as TicketItem]);
            }
        } catch {
            // Fall through to plain text parsing
        }
    }

    if (raw.includes(">") || raw.includes(":") || raw.includes("\n")) {
        const lines = raw
            .split("\n")
            .map((l) => l.trimEnd())
            .filter(Boolean);
        if (
            lines.length > 1 &&
            lines.some(
                (l) =>
                    l.startsWith(" ") || l.startsWith("\t") || l.startsWith("-")
            )
        ) {
            const result: TicketItem[] = [];
            let currentParent: TicketItem | null = null;

            lines.forEach((line) => {
                const isSub =
                    line.startsWith(" ") ||
                    line.startsWith("\t") ||
                    line.trimStart().startsWith("-");
                const cleanKey = line.replace(/^[\s\-*>]+/, "").trim();
                if (!cleanKey) {
                    return;
                }

                const item: TicketItem = { key: cleanKey };
                if (isSub && currentParent) {
                    currentParent.subtasks = currentParent.subtasks || [];
                    currentParent.subtasks.push(item);
                } else {
                    currentParent = item;
                    result.push(item);
                }
            });

            if (result.length > 0) {
                return result;
            }
        }

        const segments = raw
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean);
        const result: TicketItem[] = [];

        segments.forEach((seg) => {
            const sep = seg.includes(">")
                ? ">"
                : seg.includes(":")
                  ? ":"
                  : null;
            if (sep) {
                const parts = seg.split(sep);
                const parentKey = parts[0]?.trim();
                const childrenStr = parts[1]?.trim();

                if (parentKey) {
                    const subtasks: TicketItem[] = (
                        childrenStr ? childrenStr.split(",") : []
                    )
                        .map((k) => k.trim())
                        .filter(Boolean)
                        .map((k) => ({ key: k }));

                    result.push({
                        key: parentKey,
                        subtasks: subtasks.length > 0 ? subtasks : undefined,
                    });
                }
            } else {
                result.push({ key: seg });
            }
        });

        if (result.length > 0) {
            return result;
        }
    }

    const keys = raw.split(/[\s,]+/).filter(Boolean);
    return keys.map((key) => ({ key }));
};

export const removeTicketFromRaw = (
    raw: string | null | undefined,
    targetKey: string
): string | null => {
    if (!raw || !raw.trim()) {
        return null;
    }

    const trimmed = raw.trim();

    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                const filtered = parsed
                    .filter((item: TicketItem) => item.key !== targetKey)
                    .map((item: TicketItem) => {
                        if (item.subtasks) {
                            return {
                                ...item,
                                subtasks: item.subtasks.filter(
                                    (sub: TicketItem) => sub.key !== targetKey
                                ),
                            };
                        }
                        return item;
                    });
                return filtered.length > 0 ? JSON.stringify(filtered) : null;
            }
        } catch {
            // fallback
        }
    }

    if (trimmed.includes("\n")) {
        const lines = trimmed.split("\n");
        const filteredLines = lines.filter((line) => {
            const cleanKey = line.replace(/^[\s\-*>]+/, "").trim();
            return cleanKey !== targetKey;
        });
        return filteredLines.length > 0 ? filteredLines.join("\n") : null;
    }

    if (trimmed.includes(",") || trimmed.includes(";")) {
        const sep = trimmed.includes(";") ? ";" : ",";
        const parts = trimmed
            .split(sep)
            .map((p) => p.trim())
            .filter(Boolean);
        const filteredParts = parts.filter((part) => {
            if (part.includes(">") || part.includes(":")) {
                const pSep = part.includes(">") ? ">" : ":";
                const [parent, children] = part.split(pSep);
                if (parent.trim() === targetKey) {
                    return false;
                }
                const subKeys = (children ?? "")
                    .split(",")
                    .map((k) => k.trim())
                    .filter((k) => k && k !== targetKey);
                return subKeys.length > 0
                    ? `${parent.trim()} ${pSep} ${subKeys.join(", ")}`
                    : parent.trim();
            }
            return part !== targetKey;
        });
        return filteredParts.length > 0 ? filteredParts.join(`${sep} `) : null;
    }

    if (trimmed === targetKey) {
        return null;
    }

    return trimmed;
};

export const TicketsSection: React.FC<TicketsSectionProps> = ({
    jiraKey,
    tickets,
    metadataTickets,
    onAdd,
    onDelete,
    canAdd = false,
    canDelete = false,
}) => {
    const { t } = useTranslation("global");
    const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
    const [ticketToDelete, setTicketToDelete] = useState<TicketItem | null>(
        null
    );
    const [isDeletingTicket, setIsDeletingTicket] = useState<boolean>(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
    const [newTicketKey, setNewTicketKey] = useState<string>("");
    const [newTicketTitle, setNewTicketTitle] = useState<string>("");
    const [newTicketParent, setNewTicketParent] = useState<string>("");
    const [isAddingTicket, setIsAddingTicket] = useState<boolean>(false);
    const [addError, setAddError] = useState<string | null>(null);
    const [search, setSearch] = useState<string>("");

    const ticketList = useMemo(() => {
        return parseTicketsList(jiraKey, tickets, metadataTickets);
    }, [jiraKey, tickets, metadataTickets]);

    const filteredTicketList = useMemo(() => {
        return filterTicketsBySearch(ticketList, search);
    }, [ticketList, search]);

    const isExpanded = (key: string): boolean => {
        return openMap[key] ?? true;
    };

    const toggleExpanded = (key: string): void => {
        setOpenMap((prev) => ({
            ...prev,
            [key]: !isExpanded(key),
        }));
    };

    const handleConfirmAdd = async (): Promise<void> => {
        if (!newTicketKey.trim()) {
            setAddError(t("developments.modal_new.validation_required"));
            return;
        }

        if (!onAdd) {
            return;
        }

        setIsAddingTicket(true);
        setAddError(null);

        try {
            await onAdd({
                key: newTicketKey.trim(),
                title: newTicketTitle.trim() || undefined,
                parentKey: newTicketParent.trim() || undefined,
            });
            setIsAddModalOpen(false);
            setNewTicketKey("");
            setNewTicketTitle("");
            setNewTicketParent("");
        } catch (err) {
            setAddError(
                err instanceof Error ? err.message : "Error al añadir tarea"
            );
        } finally {
            setIsAddingTicket(false);
        }
    };

    const handleConfirmDelete = async (): Promise<void> => {
        if (!ticketToDelete || !onDelete) {
            return;
        }

        setIsDeletingTicket(true);

        try {
            await onDelete(ticketToDelete.key);
            setTicketToDelete(null);
        } catch (err) {
            alert(
                err instanceof Error ? err.message : "Error al eliminar tarea"
            );
        } finally {
            setIsDeletingTicket(false);
        }
    };

    return (
        <section className="bg-box rounded-2xl border border-blue-200/80 dark:border-blue-800/40 p-5 shadow-xs space-y-3">
            <header className="flex items-center justify-between pb-1">
                <div className="flex items-center gap-2">
                    <Icon value="listTodo" size={17} className="text-primary" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
                        {t("developments.detail.tickets_title")}
                    </h3>
                </div>
                <Badge variant="primary" size="sm" count>
                    {ticketList.length}
                </Badge>
            </header>

            {canAdd && Boolean(onAdd) && (
                <button
                    type="button"
                    onClick={() => setIsAddModalOpen(true)}
                    className="btn-primary w-full justify-center h-9 text-xs shadow-xs"
                >
                    <Icon value="plus" size={15} />
                    <span>{t("developments.detail.add_ticket")}</span>
                </button>
            )}

            {ticketList.length > 0 && (
                <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder={t(
                        "developments.detail.section_search_placeholder"
                    )}
                    wrapperClassName="px-0.5"
                />
            )}

            <div className="space-y-2.5">
                {ticketList.length === 0 ? (
                    <p className="py-3 text-center text-xs text-slate-400">
                        {t("developments.detail.tickets_empty")}
                    </p>
                ) : filteredTicketList.length === 0 ? (
                    <p className="py-3 text-center text-xs text-slate-400">
                        {t("developments.detail.section_search_empty")}
                    </p>
                ) : (
                    filteredTicketList.map((ticket) => {
                        const hasSubtasks = Boolean(
                            ticket.subtasks && ticket.subtasks.length > 0
                        );
                        const platform = detectPlatform(
                            ticket.key,
                            ticket.platform
                        );
                        const expanded = isExpanded(ticket.key);

                        if (hasSubtasks) {
                            return (
                                <div
                                    key={ticket.key}
                                    className="rounded-xl border border-blue-200/70 dark:border-blue-800/60 overflow-hidden bg-blue-50/20 dark:bg-blue-950/10"
                                >
                                    <div
                                        onClick={() =>
                                            toggleExpanded(ticket.key)
                                        }
                                        className="flex items-center justify-between p-2.5 bg-blue-50/60 dark:bg-blue-950/30 hover:bg-blue-100/70 dark:hover:bg-blue-900/40 transition-colors cursor-pointer select-none group"
                                    >
                                        <div className="flex items-center space-x-2 min-w-0">
                                            <span
                                                className={`p-0.5 rounded text-blue-600 dark:text-blue-400 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
                                            >
                                                <Icon
                                                    value="chevronRight"
                                                    size={15}
                                                />
                                            </span>
                                            <div className="p-1 text-blue-700 dark:text-blue-300 shrink-0">
                                                <Icon value="link" size={13} />
                                            </div>
                                            <span className="font-bold text-xs text-blue-800 dark:text-blue-300 truncate">
                                                {ticket.key}
                                            </span>
                                            {ticket.title && (
                                                <span className="text-xs text-slate-500 dark:text-zinc-400 truncate max-w-[120px]">
                                                    {ticket.title}
                                                </span>
                                            )}
                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-200/70 dark:bg-blue-900/70 text-blue-800 dark:text-blue-200 rounded-md">
                                                {ticket.subtasks!.length}{" "}
                                                {ticket.subtasks!.length === 1
                                                    ? t(
                                                          "developments.detail.subtask"
                                                      )
                                                    : t(
                                                          "developments.detail.subtasks"
                                                      )}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                            <span
                                                className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${platform.badgeClass}`}
                                            >
                                                {platform.name}
                                            </span>
                                            {/*
                                        <a
                                            href={getTicketUrl(ticket)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-blue-700 dark:text-blue-400 p-1 hover:bg-blue-200/60 dark:hover:bg-blue-800/60 rounded transition-colors group-hover:translate-x-0.5"
                                            title={ticket.key}
                                        >
                                            <Icon value="externalLink" size={14} />
                                        </a>
                                        */}
                                            {canDelete && Boolean(onDelete) && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTicketToDelete(
                                                            ticket
                                                        );
                                                    }}
                                                    className="text-red-400 hover:text-rose-600 p-1 rounded transition-colors cursor-pointer"
                                                    title={t("modal.delete")}
                                                >
                                                    <Icon
                                                        value="trash"
                                                        size={20}
                                                    />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {expanded && (
                                        <div className="p-2.5 pt-1.5 space-y-1.5 bg-white/60 dark:bg-dark-box/40 border-t border-blue-100 dark:border-blue-900/40">
                                            <div className="ml-3 pl-3 border-l-2 border-blue-200 dark:border-blue-800/70 space-y-1.5">
                                                {ticket.subtasks!.map((sub) => {
                                                    const subPlatform =
                                                        detectPlatform(
                                                            sub.key,
                                                            sub.platform
                                                        );
                                                    return (
                                                        <div
                                                            key={sub.key}
                                                            className="flex items-center justify-between py-1.5 px-2.5 rounded-lg border border-slate-200/80 dark:border-white/10 bg-white dark:bg-dark-box hover:bg-blue-50/60 dark:hover:bg-blue-950/20 transition-colors group"
                                                        >
                                                            <a
                                                                href={getTicketUrl(
                                                                    sub
                                                                )}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center space-x-2 min-w-0 flex-1 cursor-pointer"
                                                            >
                                                                <Icon
                                                                    value="chevronRight"
                                                                    size={12}
                                                                    className="text-slate-400 shrink-0"
                                                                />
                                                                <span className="font-semibold text-xs text-slate-800 dark:text-zinc-200 truncate">
                                                                    {sub.key}
                                                                </span>
                                                                {sub.title && (
                                                                    <span className="text-[11px] text-slate-500 dark:text-zinc-400 truncate max-w-[120px]">
                                                                        {
                                                                            sub.title
                                                                        }
                                                                    </span>
                                                                )}
                                                            </a>

                                                            <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                                                <span
                                                                    className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${subPlatform.badgeClass}`}
                                                                >
                                                                    {
                                                                        subPlatform.name
                                                                    }
                                                                </span>
                                                                <a
                                                                    href={getTicketUrl(
                                                                        sub
                                                                    )}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 rounded transition-colors"
                                                                    title={
                                                                        sub.key
                                                                    }
                                                                >
                                                                    <Icon
                                                                        value="externalLink"
                                                                        size={
                                                                            13
                                                                        }
                                                                    />
                                                                </a>
                                                                {canDelete &&
                                                                    Boolean(
                                                                        onDelete
                                                                    ) && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() =>
                                                                                setTicketToDelete(
                                                                                    sub
                                                                                )
                                                                            }
                                                                            className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors cursor-pointer"
                                                                            title={t(
                                                                                "modal.delete"
                                                                            )}
                                                                        >
                                                                            <Icon
                                                                                value="trash"
                                                                                size={
                                                                                    12
                                                                                }
                                                                            />
                                                                        </button>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <div
                                key={ticket.key}
                                className="flex items-center justify-between py-2 px-3 rounded-xl border border-blue-200 dark:border-blue-800/60 bg-blue-50/50 dark:bg-blue-950/20 hover:bg-blue-100/70 dark:hover:bg-blue-900/30 transition-colors group"
                            >
                                <a
                                    href={getTicketUrl(ticket)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2.5 min-w-0 flex-1 cursor-pointer"
                                >
                                    <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 shrink-0">
                                        <Icon value="link" size={14} />
                                    </div>
                                    <span className="font-bold text-xs text-blue-800 dark:text-blue-300 truncate">
                                        {ticket.key}
                                    </span>
                                    {ticket.title && (
                                        <span className="text-xs text-slate-500 dark:text-zinc-400 truncate">
                                            {ticket.title}
                                        </span>
                                    )}
                                </a>

                                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                    <span
                                        className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${platform.badgeClass}`}
                                    >
                                        {platform.name}
                                    </span>
                                    <a
                                        href={getTicketUrl(ticket)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-700 dark:text-blue-400 p-1 hover:bg-blue-200/60 dark:hover:bg-blue-800/60 rounded transition-colors"
                                        title={ticket.key}
                                    >
                                        <Icon value="externalLink" size={14} />
                                    </a>
                                    {canDelete && Boolean(onDelete) && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setTicketToDelete(ticket)
                                            }
                                            className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors cursor-pointer"
                                            title={t("modal.delete")}
                                        >
                                            <Icon value="trash" size={13} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Add Task Modal */}
            {canAdd && (
                <Modal
                    isOpen={isAddModalOpen}
                    onClose={() => {
                        if (!isAddingTicket) {
                            setIsAddModalOpen(false);
                            setNewTicketKey("");
                            setNewTicketTitle("");
                            setNewTicketParent("");
                            setAddError(null);
                        }
                    }}
                    title={t("developments.detail.add_ticket")}
                    maxWidth="md"
                    onSubmit={handleConfirmAdd}
                    isSubmitting={isAddingTicket}
                    submitLabel={t("developments.modal_new.create_button")}
                    submitDisabled={!newTicketKey.trim()}
                >
                    <div className="space-y-4">
                        {addError && (
                            <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
                                {addError}
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                                {t("developments.detail.ticket_key")} *
                            </label>
                            <input
                                type="text"
                                value={newTicketKey}
                                onChange={(e) =>
                                    setNewTicketKey(e.target.value)
                                }
                                placeholder={t(
                                    "developments.detail.ticket_key_placeholder"
                                )}
                                className="w-full h-10 px-3 text-xs rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-dark-subtle text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                autoFocus
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                                {t("developments.detail.ticket_title")}
                            </label>
                            <input
                                type="text"
                                value={newTicketTitle}
                                onChange={(e) =>
                                    setNewTicketTitle(e.target.value)
                                }
                                placeholder={t(
                                    "developments.detail.ticket_title_placeholder"
                                )}
                                className="w-full h-10 px-3 text-xs rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-dark-subtle text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>

                        {ticketList.length > 0 && (
                            <div>
                                <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                                    {t("developments.detail.ticket_parent")}
                                </label>
                                <select
                                    value={newTicketParent}
                                    onChange={(e) =>
                                        setNewTicketParent(e.target.value)
                                    }
                                    className="w-full h-10 px-3 text-xs rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-dark-subtle text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                                >
                                    <option value="">
                                        {t(
                                            "developments.detail.ticket_parent_none"
                                        )}
                                    </option>
                                    {ticketList.map((parent) => (
                                        <option
                                            key={parent.key}
                                            value={parent.key}
                                        >
                                            {parent.key}{" "}
                                            {parent.title
                                                ? `— ${parent.title}`
                                                : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {ticketToDelete && (
                <Modal
                    isOpen={Boolean(ticketToDelete)}
                    onClose={() => {
                        if (!isDeletingTicket) {
                            setTicketToDelete(null);
                        }
                    }}
                    title={t("developments.detail.delete_ticket_title")}
                    maxWidth="sm"
                    submitVariant="danger"
                    onSubmit={handleConfirmDelete}
                    isSubmitting={isDeletingTicket}
                    submitLabel={t("modal.delete")}
                    submittingLabel={t("modal.deleting")}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="flex items-start gap-3 py-2">
                        <div className="w-10 h-10 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0 border border-rose-100 dark:border-rose-900/50">
                            <Icon value="trash" size={18} />
                        </div>
                        <div className="space-y-1 min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                                {ticketToDelete.key}{" "}
                                {ticketToDelete.title
                                    ? `— ${ticketToDelete.title}`
                                    : ""}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed">
                                {t("developments.detail.delete_ticket_desc")}
                            </p>
                        </div>
                    </div>
                </Modal>
            )}
        </section>
    );
};
